import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useUser, UserButton } from '@clerk/clerk-react'
import { ReactNode } from 'react'

interface LayoutProps {
  children: ReactNode
}

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/send', label: 'Send', icon: '📤' },
  { path: '/receive', label: 'Receive', icon: '📥' },
  { path: '/discover', label: 'Discover', icon: '🔍' },
  { path: '/earn', label: 'Earn', icon: '💰' },
  { path: '/apps', label: 'Apps', icon: '📱' },
  { path: '/rewards', label: 'Rewards', icon: '🏆' },
  { path: '/profile', label: 'Profile', icon: '👤' },
  { path: '/store', label: 'Store', icon: '🛍️' },
]

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useUser()

  const isActive = (path: string) => location.pathname === path

  return (
    <div className="flex h-screen bg-slate-950">
      {/* Sidebar - Desktop: Relative, Mobile: Fixed */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-slate-900 border-r border-slate-800 flex flex-col transition-all duration-300 hidden md:flex md:relative h-full z-40`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-center border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3 px-4 w-full">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">OTD</span>
            </div>
            {sidebarOpen && (
              <div className="flex flex-col">
                <span className="text-white font-bold text-sm">OTD</span>
                <span className="text-slate-400 text-xs">Core</span>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-2">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path)
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                isActive(item.path)
                  ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
              }`}
            >
              <span className="text-xl flex-shrink-0">{item.icon}</span>
              {sidebarOpen && <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* User Profile Section */}
        <div className="h-16 border-t border-slate-800 flex items-center justify-center gap-2 px-3 flex-shrink-0">
          {user && (
            <div className="flex-1 flex items-center gap-2 min-w-0">
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {user.firstName || 'User'}
                  </p>
                  <p className="text-slate-400 text-xs truncate">
                    {user.emailAddresses[0]?.emailAddress.split('@')[0]}
                  </p>
                </div>
              )}
              <UserButton
                appearance={{
                  elements: {
                    rootBox: 'flex',
                    userButtonAvatarBox: 'h-8 w-8',
                  },
                }}
              />
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Sidebar - Fixed Overlay */}
      <div
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } fixed md:hidden left-0 top-0 w-64 h-screen bg-slate-900 border-r border-slate-800 flex flex-col transition-transform duration-300 z-40`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-center border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3 px-4 w-full">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">OTD</span>
            </div>
            <div className="flex flex-col">
              <span className="text-white font-bold text-sm">OTD</span>
              <span className="text-slate-400 text-xs">Core</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-2">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path)
                setSidebarOpen(false)
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                isActive(item.path)
                  ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
              }`}
            >
              <span className="text-xl flex-shrink-0">{item.icon}</span>
              <span className="text-sm font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* User Profile Section */}
        <div className="h-16 border-t border-slate-800 flex items-center justify-center gap-2 px-3 flex-shrink-0">
          {user && (
            <div className="flex-1 flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {user.firstName || 'User'}
                </p>
                <p className="text-slate-400 text-xs truncate">
                  {user.emailAddresses[0]?.emailAddress.split('@')[0]}
                </p>
              </div>
              <UserButton
                appearance={{
                  elements: {
                    rootBox: 'flex',
                    userButtonAvatarBox: 'h-8 w-8',
                  },
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 md:hidden z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden w-full">
        {/* Top Bar */}
        <header className="h-16 border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm flex items-center justify-between px-4 md:px-6 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-white"
          >
            {sidebarOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-4">
            <p className="text-slate-400 text-sm hidden sm:block">
              {new Date().toLocaleDateString('es-ES', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-slate-950">
          <div className="w-full p-4 md:p-6">{children}</div>
        </div>
      </main>
    </div>
  )
}
